import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface CustomerStatCardProps {
  label: string;
  value: number;
  icon: IconDefinition;
  to?: string;
  hint?: string;
  /** Entrance-stagger index (each card offset ~40ms) — first-load only, see index.css. */
  index?: number;
}

const SHARED_CLS =
  'customer-fade-in customer-card-interactive customer-pressable flex min-h-[6.25rem] flex-col justify-between gap-3 rounded-customer border border-customer-border bg-customer-surface p-4 shadow-customer-sm';

/**
 * Compact metric tile. On a 320px phone two fit side by side with the number
 * still readable; the icon tile and the optional chevron tell the eye which
 * tiles are links without adding text.
 */
export function CustomerStatCard({
  label,
  value,
  icon,
  to,
  hint,
  index = 0,
}: CustomerStatCardProps) {
  const style = { animationDelay: `${index * 40}ms` };

  const content = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-customer-sm bg-customer-primary/10 text-customer-primary-text"
          aria-hidden="true"
        >
          <FontAwesomeIcon icon={icon} className="h-4 w-4" />
        </span>
        {to && (
          <FontAwesomeIcon
            icon={faChevronRight}
            className="mt-1 h-3 w-3 text-customer-text-secondary"
            aria-hidden="true"
          />
        )}
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-semibold leading-none tabular-nums tracking-tight text-customer-text">
          {value}
        </p>
        <p className="mt-1.5 text-xs font-medium text-customer-text-secondary">{label}</p>
        {hint && <p className="mt-0.5 truncate text-[11px] text-customer-text-secondary">{hint}</p>}
      </div>
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        style={style}
        className={`${SHARED_CLS} hover:border-customer-primary/40 hover:shadow-customer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring focus-visible:ring-offset-2 focus-visible:ring-offset-customer-bg`}
      >
        {content}
      </Link>
    );
  }

  return (
    <div style={style} className={SHARED_CLS}>
      {content}
    </div>
  );
}
